(() => {
  'use strict';

  if (window.antProductVariantOptionsInitialized) {
    return;
  }

  window.antProductVariantOptionsInitialized = true;

  const LEGEND_SELECTOR =
    '.hdt-product-form__input.is-style-color legend.hdt-product-form__label, ' +
    '.hdt-product-form__input.is-style-color legend';
  const EXPAND_KEY = 'ant45:color-swatches-expanded';
  const HIDDEN_SWATCH_CLASSES = [
    'hdt-color-swatch-hidden-mobile',
    'hdt-color-swatch-hidden-desktop',
    'hdt-swatch-hide-mobile',
    'hdt-swatch-hide-desktop'
  ];

  const seen = new WeakMap();
  const updating = new WeakSet();

  function stripBeforeUnderscore(s) {
    if (!s) return s;

    const i = s.indexOf('_');

    return i === -1 ? s : s.slice(i + 1).replace(/_/g, ' ');
  }

  function normalizeLegend(legend) {
    if (!legend || updating.has(legend)) return;

    const valueEl = legend.querySelector('span[update-value], [update-value]');

    if (valueEl) {
      const current = (valueEl.textContent || '').trim();
      const next = stripBeforeUnderscore(current).trim();

      if (next && next !== current) {
        updating.add(legend);
        valueEl.textContent = next;
        queueMicrotask(() => updating.delete(legend));
      }

      return;
    }

    const txt = legend.textContent || '';
    const match = txt.match(/([A-Za-z0-9]+_[A-Za-z0-9_]+)\s*$/);

    if (!match) return;

    const token = match[1];
    const replaced = stripBeforeUnderscore(token);

    if (replaced !== token) {
      updating.add(legend);
      legend.textContent = txt.replace(token, replaced);
      queueMicrotask(() => updating.delete(legend));
    }
  }

  function attachLegendObserver(legend) {
    if (!legend || seen.has(legend)) return;

    normalizeLegend(legend);

    const obs = new MutationObserver(() => normalizeLegend(legend));
    obs.observe(legend, { subtree: true, childList: true, characterData: true });

    seen.set(legend, obs);
  }

  function scan(root = document) {
    root.querySelectorAll(LEGEND_SELECTOR).forEach(attachLegendObserver);
  }

  function getExpandedGroups() {
    try {
      const stored = JSON.parse(sessionStorage.getItem(EXPAND_KEY) || '[]');
      return Array.isArray(stored) ? stored : [];
    } catch (err) {
      return [];
    }
  }

  function saveExpandedGroup(group) {
    if (!group) return;

    const groups = getExpandedGroups();

    if (groups.includes(group)) return;

    groups.push(group);
    sessionStorage.setItem(EXPAND_KEY, JSON.stringify(groups));
  }

  function expandGroup(group) {
    if (!group) return;

    document.querySelectorAll('[data-color-option-group]').forEach(item => {
      if (item.dataset.colorOptionGroup !== group) return;

      item.classList.remove(...HIDDEN_SWATCH_CLASSES);
    });

    document.querySelectorAll('[data-color-option-toggle]').forEach(button => {
      if (button.dataset.colorOptionToggle !== group) return;

      const wrap = button.closest('.hdt-swatch-item.is--color-link');
      (wrap || button).remove();
    });
  }

  function restoreExpanded() {
    getExpandedGroups().forEach(expandGroup);
  }

  function withVariantParam(url, variantId) {
    if (!url || !variantId) return url || '';

    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('variant', String(variantId));
      return parsed.pathname + parsed.search + parsed.hash;
    } catch (err) {
      return url;
    }
  }

  function syncCardProductLinks(card, swatch, variantId) {
    if (!card || !variantId) return;

    const base =
      swatch?.dataset.productUrl ||
      card.querySelector('[data-pr-url]')?.getAttribute('href');
    const href = withVariantParam(base, variantId);

    if (!href) return;

    card.querySelectorAll('[data-pr-url]').forEach(link => {
      link.href = href;
    });
  }

  function moneyText(el) {
    return (el?.textContent || '').replace(/\s+/g, '');
  }

  function moneyLooksZero(el) {
    const digits = (el?.textContent || '').replace(/[^\d]/g, '');
    return digits === '' || /^0+$/.test(digits);
  }

  function variantIsOnSale(variant) {
    if (!variant) return false;

    const price = Number(variant.price);
    const compare = Number(variant.compare_at_price);

    return Number.isFinite(compare) && compare > 0 && Number.isFinite(price) && compare > price;
  }

  function hideEqualComparePrice(root, variant) {
    if (!(root instanceof Element)) return;

    root.querySelectorAll('hdt-compare-at-price').forEach(compareEl => {
      if (updating.has(compareEl)) return;

      const list = compareEl.closest('.hdt-price__list') || compareEl.parentElement;
      const priceMoney = list?.querySelector('hdt-price .hdt-money');
      const compareMoney = compareEl.querySelector('.hdt-money');
      const sameText = Boolean(moneyText(compareMoney) && moneyText(compareMoney) === moneyText(priceMoney));
      const hide =
        moneyLooksZero(compareMoney) || (variant ? !variantIsOnSale(variant) : sameText);

      if (compareEl.hidden === hide && compareEl.classList.contains('hdt-hidden') === hide) {
        return;
      }

      updating.add(compareEl);
      compareEl.hidden = hide;
      compareEl.classList.toggle('hdt-hidden', hide);
      if (hide) {
        compareEl.setAttribute('hidden', '');
      } else {
        compareEl.removeAttribute('hidden');
      }
      queueMicrotask(() => updating.delete(compareEl));
    });
  }

  function scanEqualComparePrices(root = document) {
    root.querySelectorAll?.('.hdt-price__list')?.forEach(list => hideEqualComparePrice(list));
  }

  function observeComparePriceEls(root = document) {
    root.querySelectorAll?.('hdt-compare-at-price').forEach(el => {
      if (seen.has(el)) return;

      const obs = new MutationObserver(() => {
        hideEqualComparePrice(el.closest('.hdt-price__list') || el);
      });
      obs.observe(el, {
        attributes: true,
        attributeFilter: ['hidden', 'class'],
        childList: true,
        subtree: true,
        characterData: true
      });
      seen.set(el, obs);
    });
  }

  function variantFromEvent(event) {
    const detail = event?.detail;
    if (detail?.variant && typeof detail.variant === 'object') return detail.variant;
    if (detail && typeof detail === 'object' && 'price' in detail) return detail;
    return undefined;
  }

  function applySwatchCardImage(card, swatch) {
    const src = swatch?.dataset.cardSrc;
    const img = card?.querySelector('.hdt-card-product__media--main');

    if (!src || !img) return;

    img.src = src;
    img.srcset = src;
  }

  function getCommittedSwatch(card) {
    if (!card) return null;

    const id = card.dataset.antCommittedVrid;
    if (id) {
      const committed = card.querySelector(
        `.hdt-variant-option--color hdt-swatch-card[data-vrid="${id}"]`
      );
      if (committed) return committed;
    }

    return card.querySelector(
      '.hdt-variant-option--color .hdt-swatch-item.active:not(.is--color-link) hdt-swatch-card'
    );
  }

  function setActiveSwatch(swatch) {
    const item = swatch?.closest('.hdt-swatch-item');
    const list = swatch?.closest('.hdt-variants-list');

    if (!item || !list || item.classList.contains('is--color-link')) return;

    list.querySelectorAll(':scope > .hdt-swatch-item.active').forEach(el => {
      el.classList.remove('active');
    });
    item.classList.add('active');
  }

  function commitCardSwatch(card, swatch, applyImage) {
    if (!card || !swatch) return;

    card.dataset.antCommittedVrid = swatch.dataset.vrid || '';
    setActiveSwatch(swatch);
    syncCardProductLinks(card, swatch, swatch.dataset.vrid);
    if (applyImage) applySwatchCardImage(card, swatch);
  }

  function restoreCommittedSwatch(card) {
    const swatch = getCommittedSwatch(card);

    if (!swatch) return;

    setActiveSwatch(swatch);
    syncCardProductLinks(card, swatch, swatch.dataset.vrid);
    requestAnimationFrame(() => applySwatchCardImage(card, swatch));
  }

  function syncInitialCardSwatches(root = document) {
    root.querySelectorAll?.('hdt-card-product').forEach(card => {
      if (card.dataset.antCommittedVrid) return;

      const swatch =
        card.querySelector(
          '.hdt-variant-option--color .hdt-swatch-item.active:not(.is--color-link) hdt-swatch-card'
        ) ||
        card.querySelector(
          '.hdt-variant-option--color .hdt-swatch-item:not(.is--color-link) hdt-swatch-card'
        );

      if (swatch) commitCardSwatch(card, swatch, true);
    });
  }

  function activateCardSwatch(swatch) {
    const item = swatch.closest('.hdt-swatch-item');
    const card = swatch.closest('hdt-card-product');

    if (!item || item.classList.contains('is--color-link')) return;

    setActiveSwatch(swatch);
    syncCardProductLinks(card, swatch, swatch.dataset.vrid);
    applySwatchCardImage(card, swatch);
  }

  function patchCardSwatches() {
    const Ctor = customElements.get('hdt-swatch-card');

    if (!Ctor || Ctor.prototype.__antSwatchPatched) return;

    const proto = Ctor.prototype;
    proto.__antSwatchPatched = true;

    const origUpdatePrice = proto.updatePrice;
    if (typeof origUpdatePrice === 'function') {
      proto.updatePrice = function updatePrice(variant, card = this) {
        origUpdatePrice.call(this, variant, card);
        const root = card instanceof Element ? card : this.closest('hdt-card-product');
        hideEqualComparePrice(root, variant);
      };
    }

    proto.handleClick = function handleClick() {
      const card = this.closest('hdt-card-product');
      const item = this.closest('.hdt-swatch-item');
      const list = this.closest('.hdt-variants-list');

      list?.querySelectorAll('.hdt-swatch-item.active').forEach(el => el.classList.remove('active'));
      item?.classList.add('active');
      card?.classList.add('loading');
      syncCardProductLinks(card, this, this.dataset.vrid);
      commitCardSwatch(card, this);

      if (this.dataset.valueId) {
        this.debouncedUpdate(this.getActiveDataValueIds().join(','));
      }
    };

    proto.processSwatchResponse = function processSwatchResponse(html) {
      const card = this.closest('hdt-card-product');
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const selected = doc.getElementById('selected-variant');

      if (!selected) {
        card?.classList.remove('loading');
        return;
      }

      try {
        this._currentVariant = JSON.parse(selected.textContent);
        const nextMedia = selected.getAttribute('data-next-media');
        this._currentVariant.next_media = nextMedia ? JSON.parse(nextMedia) : null;
      } catch (err) {
        card?.classList.remove('loading');
        return;
      }

      if (this._inputId) this._inputId.value = this._currentVariant.id;

      this.updateATC(this._currentVariant, card);
      this.rebuildImage(this._currentVariant, card);
      this.updatePrice(this._currentVariant, card);
      syncCardProductLinks(card, this, this._currentVariant.id);
      commitCardSwatch(card, this);

      this.closest('.hdt-swatch-item')?.classList.add('active');
      card?.classList.remove('loading');
      this.dispatchEvent(
        new CustomEvent('variant:updated', {
          detail: { variant: this._currentVariant },
          bubbles: true,
          cancelable: true
        })
      );
    };
  }

  function start() {
    scan();
    restoreExpanded();
    patchCardSwatches();
    scanEqualComparePrices();
    observeComparePriceEls();
    syncInitialCardSwatches();

    const rootObs = new MutationObserver(mutations => {
      let shouldRestore = false;
      let shouldSyncCards = false;
      let shouldSyncPrices = false;

      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (!(node instanceof Element)) return;

          if (node.matches?.(LEGEND_SELECTOR)) {
            attachLegendObserver(node);
          }

          scan(node);

          if (
            node.matches?.('[data-color-option-group], [data-color-option-toggle], hdt-variant-picker') ||
            node.querySelector?.('[data-color-option-group], [data-color-option-toggle], hdt-variant-picker')
          ) {
            shouldRestore = true;
          }

          if (node.matches?.('hdt-card-product') || node.querySelector?.('hdt-card-product')) {
            shouldSyncCards = true;
          }

          if (
            node.matches?.('hdt-compare-at-price, .hdt-price__list, .hdt-price-wrapp') ||
            node.querySelector?.('hdt-compare-at-price')
          ) {
            shouldSyncPrices = true;
          }
        });
      });

      if (shouldRestore) restoreExpanded();
      if (shouldSyncCards) {
        scanEqualComparePrices();
        observeComparePriceEls();
        syncInitialCardSwatches();
      } else if (shouldSyncPrices) {
        scanEqualComparePrices();
        observeComparePriceEls();
      }
    });

    rootObs.observe(document.documentElement, { childList: true, subtree: true });
  }

  document.addEventListener('click', event => {
    const button = event.target.closest('[data-color-option-toggle]');

    if (!button) return;

    const group = button.dataset.colorOptionToggle;
    saveExpandedGroup(group);
    expandGroup(group);
    event.preventDefault();
  });

  // Desktop hover: allinea bordo, URL e foto allo swatch; al leave torna alla variante confermata.
  document.addEventListener('pointerover', event => {
    const swatch = event.target.closest?.('hdt-card-product .hdt-variant-option--color hdt-swatch-card');

    if (!swatch) return;

    activateCardSwatch(swatch);
  });

  document.addEventListener('pointerout', event => {
    const card = event.target.closest?.('hdt-card-product');

    if (!card) return;

    const related = event.relatedTarget;
    if (related instanceof Node && card.contains(related)) return;

    restoreCommittedSwatch(card);
  });

  ['variant:update', 'variant:change', 'variant:selected'].forEach(name => {
    document.addEventListener(
      name,
      event => {
        const variant = variantFromEvent(event);
        document.querySelectorAll('.hdt-price__list').forEach(list => {
          hideEqualComparePrice(list, variant);
        });
      },
      true
    );
  });

  if (customElements.get('hdt-swatch-card')) {
    patchCardSwatches();
  } else {
    customElements.whenDefined('hdt-swatch-card').then(patchCardSwatches);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
